/**
 * The account popover — design "Led AI - Console Sign In", 3a.
 *
 * ## What this is, and what it deliberately is not
 *
 * 3a draws the menu that opens off the sidebar footer's avatar: an identity
 * block, "Manage account", and "Sign out" in red. The artboard's own note
 * calls it "the Clerk user-button popover … styled as a resting Led AI card",
 * and that is exactly what this file does — it themes Clerk's `<UserButton>`
 * through its `appearance` API rather than rebuilding the menu.
 *
 * Rebuilding it was the obvious alternative and it is the wrong trade, for
 * the reason written at length in `identity.tsx`: `<UserButton>` is not a
 * sign-out button with an avatar on it. It is also how a user ADDS a second
 * account and switches between them, and switching accounts is the only
 * route to an organization the current account does not belong to — half the
 * console's workspace behaviour is unreachable without it. A hand-built
 * popover over `useClerk().signOut()` would have pixel-for-pixel control of
 * the artboard and would quietly delete that.
 *
 * ## The one thing 3a asks for that is not here
 *
 * The artboard puts a role Pill ("Admin") to the right of the email. It is
 * not in this implementation and it cannot be: the identity block is Clerk's
 * `userPreview`, whose children Clerk renders, and `appearance` styles
 * elements — it cannot insert one. The console does know the caller's role,
 * but only inside `Invite`'s own payload (`members[].isYou` / `.role`), which
 * is a fetch this popover does not make. Styling the preview to the
 * artboard's type scale gets everything else; the pill would cost the
 * multi-session control above to gain.
 *
 * ## Where the rest of it lives
 *
 * Two things `appearance` cannot reach are in `main.tsx`'s stylesheet
 * instead — the ink ring the trigger wears WHILE the popover is open (a
 * state Clerk exposes only as `aria-expanded` on the trigger) and the
 * "Secured by Clerk" footer (no element key, hashed `cl-internal-*` classes).
 * Same arrangement, and the same reasoning, as the sign-in screen's borrowed
 * surface; see that block's comment.
 */

import { C, FONT_TEXT } from "../tokens";

/** 3a's card width. The artboard draws 280 against a 240px rail, which is
 *  the console's own rail width — so the popover clears the sidebar by the
 *  same margin here as it does there. */
const CARD_WIDTH = 280;

/** Both rows. 44px is the artboard's, and it is a size a pointer finds
 *  without aiming — the row IS the target, edge to edge, which is why the
 *  padding is on the button rather than on a child. */
const ROW_HEIGHT = 44;

/** Every row in the menu — Clerk's own items and the custom Integrations one. */
const ROW = {
  height: ROW_HEIGHT,
  // Clerk sizes its rows from padding, so a height alone computes back
  // down the way the sign-in field's did.
  minHeight: ROW_HEIGHT,
  padding: "0 16px",
  gap: 12,
  margin: 0,
  borderRadius: 0,
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: "-0.1px",
  color: C.ink,
  "&:hover": { background: C.sunk },
  "&:focus-visible": { background: C.sunk, outline: "none" },
};

/**
 * Clerk's `<UserButton>`, dressed as 3a.
 *
 * Nested `&:hover` selectors work here the same way they do in
 * `AUTH_APPEARANCE` — Clerk runs these through emotion. What can still go
 * wrong is that one of Clerk's own rules outranks a key (the sign-in field's
 * `height` did exactly that), which is why anything load-bearing gets
 * measured in a browser rather than trusted because it type-checks.
 */
export const USER_MENU_APPEARANCE = {
  layout: {
    // The "Development mode" strip Clerk prints under a `pk_test_` key. On
    // staging it sat inside this card and read as though the account menu
    // were a prototype; which key is in use is `/__env`'s answer, not a
    // badge's. Same call as the sign-in screen's.
    unsafe_disableDevelopmentModeWarnings: true,
  },
  variables: {
    colorPrimary: C.ink,
    colorText: C.ink,
    colorTextSecondary: C.sub,
    colorBackground: C.surface,
    colorDanger: C.red,
    fontFamily: FONT_TEXT,
    borderRadius: "12px",
  },
  elements: {
    // ---------------------------------------------------------------
    // The trigger, in the sidebar footer
    // ---------------------------------------------------------------
    /** 32px, as 3a draws it beside the name. Clerk's default is 28 and it
     *  read as an afterthought next to a 15px name. */
    userButtonAvatarBox: { width: 32, height: 32 },
    userButtonTrigger: {
      borderRadius: 999,
      // Clerk's default focus ring is its own blue halo. The console's focus
      // treatment is ink, and this control sits on the lit rail where blue
      // is the one hue that belongs to nothing else on screen.
      "&:focus-visible": {
        outline: "none",
        boxShadow: `0 0 0 3px ${C.railGround}, 0 0 0 5px ${C.ink}`,
      },
    },

    // ---------------------------------------------------------------
    // The card
    // ---------------------------------------------------------------
    userButtonPopoverCard: {
      width: CARD_WIDTH,
      background: C.surface,
      border: `1px solid ${C.popLine}`,
      borderRadius: 16,
      // The console's two-layer lift — a near shadow that seats the edge and
      // a far one that raises the card — with 3a's own far values. One hard
      // shadow is what the redesign took out everywhere else.
      boxShadow: `0 1px 2px ${C.hairRow}, 0 18px 40px ${C.popShadow}`,
      // The rows run edge to edge and their hover fill has to stop at the
      // card's corner radius rather than square it off.
      overflow: "hidden",
    },
    userButtonPopoverMain: {
      background: "transparent",
      borderRadius: 0,
      boxShadow: "none",
      border: "none",
    },

    // ---------------------------------------------------------------
    // Who you are
    // ---------------------------------------------------------------
    userPreview: { padding: "16px 16px 14px", gap: 12 },
    userPreviewAvatarBox: { width: 40, height: 40 },
    userPreviewMainIdentifier: {
      fontSize: 15,
      fontWeight: 800,
      letterSpacing: "-0.3px",
      color: C.ink,
    },
    userPreviewSecondaryIdentifier: {
      fontSize: 12.5,
      color: C.sub,
      marginTop: 2,
      // An address is the one string here that can outrun 280px, and a
      // wrapped email pushes the rows down and makes the card ragged.
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    // ---------------------------------------------------------------
    // What you can do
    // ---------------------------------------------------------------
    /** The block of rows, ruled off from the identity above it. 3a's
     *  hairline is 7% ink, which is `hair` — the rail's own weight, not the
     *  10% a card uses to separate itself from the page. */
    userButtonPopoverActions: {
      borderTop: `1px solid ${C.hair}`,
      padding: 0,
      gap: 0,
    },
    userButtonPopoverActionButton: ROW,
    // Integrations (`AuthGate`'s `UserButton.Action`) is a CUSTOM item, which
    // Clerk renders as its own element. Without these it fell back to Clerk's
    // defaults — a grey band, lighter text, a wider icon box — and read as
    // disabled between two live rows. Same row, same icon box.
    userButtonPopoverCustomItemButton: { ...ROW, background: "transparent" },
    userButtonPopoverCustomItemButtonIconBox: { width: 16, height: 16, margin: 0, color: C.ink, opacity: 1 },
    userButtonPopoverActionButtonIconBox: { width: 16, height: 16, margin: 0 },
    userButtonPopoverActionButtonIcon: { width: 16, height: 16, color: C.ink, opacity: 1 },
    /** Each row draws its own top edge rather than the block drawing
     *  dividers, so a row Clerk chooses not to render (this menu has a third
     *  in multi-session mode) cannot leave a rule with nothing under it. */
    userButtonPopoverActionButton__signOut: {
      borderTop: `1px solid ${C.hair}`,
      color: C.red,
      "&:hover": { background: C.redSoft },
      "&:focus-visible": { background: C.redSoft, outline: "none" },
    },
    userButtonPopoverActionButtonIcon__signOut: { color: C.red },

    // ---------------------------------------------------------------
    // What the card ends on
    // ---------------------------------------------------------------
    /** 3a ends on "Sign out". Clerk's footer carries its own branding row,
     *  which `main.tsx` also hides by selector — this key is the supported
     *  way to say it and the rule there is the one that has to survive a
     *  class rename. */
    userButtonPopoverFooter: { display: "none" },
  },
};
